import React, { useRef, useEffect, useState } from 'react';
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import '../map.css';
import { colors } from '../color';
import {
    EnvironmentOutlined,
    PlusOutlined,
    CheckOutlined,
    CloseOutlined,
    AimOutlined,
    HomeOutlined,
    FlagOutlined,
    DeleteOutlined,
    SwapOutlined,
    MinusCircleOutlined,
    PlusCircleOutlined,
    SearchOutlined,
    UnorderedListOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { ServerIP } from '../component/ServerIP';


const FeeShippingMap = () => {

    const mapContainer = useRef(null);
    const map = useRef(null);

    const [pickupLocation, setPickupLocation] = useState(null);
    const [deliveryLocation, setDeliveryLocation] = useState(null);
    const [isPicking, setIsPicking] = useState(null); // null, 'pickup', 'delivery'
    const [useApiRoute, setUseApiRoute] = useState(false); // false = arc, true = API
    const [searchMode, setSearchMode] = useState(false); // false = buttons, true = search
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [radius, setRadius] = useState(1000);
    const [feeShipping, setFeeShipping] = useState(0);
    const [pricing, setPricing] = useState({ basePrice: 150, pricePerKm: 130, loaded: false });
    const [routeInfo, setRouteInfo] = useState({ distance: 0, duration: 0 });

    const pickupMarker = useRef(null);
    const deliveryMarker = useRef(null);
    const circleRef = useRef(null);
    const arcLine = useRef(null);
    const apiRoutePolyline = useRef(null);
    const watchId = useRef(null);
    const apiRouteCache = useRef(null); // Cache pour éviter les appels API répétés
    const hasInitializedLocation = useRef(false); // Track if we've done initial positioning

    const tomtomApiKey = process.env.REACT_APP_TOMTOM_API_KEY;

    const upFunction = () => {
        setRadius(prev => prev + 1000);
    };

    const downFunction = () => {
        if (radius > 1000) {
            setRadius(prev => prev - 1000);
        }
    };

    const [zoom] = useState(12);

    // Fake address data for autocomplete
    const fakeAddresses = [
        { name: 'Place Boyer, Pétion-Ville', lat: 18.5125, lon: -72.2853 },
        { name: 'Champ de Mars, Port-au-Prince', lat: 18.5391, lon: -72.3377 },
        { name: 'Marché en Fer, Port-au-Prince', lat: 18.5456, lon: -72.3387 },
        { name: 'Aéroport International Toussaint Louverture', lat: 18.5800, lon: -72.2925 },
        { name: 'Université d’État d’Haïti', lat: 18.5434, lon: -72.3387 },
        { name: 'Hôpital de l’Université d’État d’Haïti', lat: 18.5411, lon: -72.3386 },
        { name: 'Cathédrale Notre-Dame de Port-au-Prince', lat: 18.5458, lon: -72.3363 },
        { name: 'Palais National (Ruines)', lat: 18.5384, lon: -72.3377 },
        { name: 'Musée du Panthéon National Haïtien', lat: 18.5391, lon: -72.3377 },
        { name: 'Rue Lamarre, Pétion-Ville', lat: 18.5089, lon: -72.2856 }
    ];

    const filteredAddresses = searchQuery.length > 0
        ? fakeAddresses.filter(addr =>
            addr.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    // 📏 Fonction haversine pour calculer la distance à vol d'oiseau entre deux points GPS
    const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Rayon de la Terre en km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance en km
    };

    // 🌀 Fonction pour créer un arc de cercle entre deux points GPS
    const createArcCoordinates = (startLat, startLon, endLat, endLon, numPoints = 50) => {
        const coordinates = [];
        coordinates.push([startLat, startLon]);

        const midLat = (startLat + endLat) / 2;
        const midLon = (startLon + endLon) / 2;

        const deltaLat = endLat - startLat;
        const deltaLon = endLon - startLon;

        const arcHeight = 0.2;
        const controlLat = midLat + deltaLon * arcHeight;
        const controlLon = midLon - deltaLat * arcHeight;

        for (let i = 1; i < numPoints; i++) {
            const t = i / numPoints;
            const lat = Math.pow(1 - t, 2) * startLat + 2 * (1 - t) * t * controlLat + Math.pow(t, 2) * endLat;
            const lon = Math.pow(1 - t, 2) * startLon + 2 * (1 - t) * t * controlLon + Math.pow(t, 2) * endLon;
            coordinates.push([lat, lon]);
        }

        coordinates.push([endLat, endLon]);
        return coordinates;
    };

    // 🚗 Fonction pour calculer la route via TomTom API
    const calculateApiRoute = async (pickup, delivery) => {
        // Vérifier si on a déjà les données en cache pour ces coordonnées
        const cacheKey = `${pickup[0]},${pickup[1]}-${delivery[0]},${delivery[1]}`;
        if (apiRouteCache.current && apiRouteCache.current.key === cacheKey) {
            console.log("✅ Utilisation du cache API - Pas d'appel réseau");
            const cached = apiRouteCache.current;

            // Display cached route
            if (apiRoutePolyline.current) {
                apiRoutePolyline.current.remove();
            }
            apiRoutePolyline.current = L.polyline(cached.coordinates, {
                color: colors.secondary,
                weight: 4
            }).addTo(map.current);

            if (arcLine.current) {
                arcLine.current.remove();
                arcLine.current = null;
            }

            setRouteInfo(cached.routeInfo);
            setFeeShipping(cached.price);
            return;
        }

        console.log("🌐 Appel API TomTom...");
        const url = `https://api.tomtom.com/routing/1/calculateRoute/${pickup[0]},${pickup[1]}:${delivery[0]},${delivery[1]}/json?key=${tomtomApiKey}`;

        try {
            const response = await fetch(url);
            const data = await response.json();

            console.log("TomTom API Response:", data);

            if (!data.routes || data.routes.length === 0) {
                throw new Error('Aucune route trouvée par l\'API');
            }

            const route = data.routes[0];
            const summary = route.summary;
            const distanceMeters = summary.lengthInMeters;
            const vitesse = 300;
            let temps = distanceMeters / vitesse;
            temps = Math.max(1, Math.round(temps));

            const distanceKm = distanceMeters / 1000;

            const coordinates = route.legs[0].points.map(point => [point.latitude, point.longitude]);

            const routeInfo = {
                distance: distanceKm,
                duration: temps
            };

            // Calculate price
            const base = pricing.basePrice;
            const perKm = 85; // API utilise 85 HTG/km (plus précis)

            let finalPrice = 0;
            if (distanceKm <= 1) {
                finalPrice = base;
            } else {
                finalPrice = Math.ceil(distanceKm) * perKm;
            }

            // Mettre en cache les résultats
            apiRouteCache.current = {
                key: cacheKey,
                coordinates: coordinates,
                routeInfo: routeInfo,
                price: finalPrice
            };

            // Display API route
            if (apiRoutePolyline.current) {
                apiRoutePolyline.current.remove();
            }
            apiRoutePolyline.current = L.polyline(coordinates, {
                color: colors.secondary,
                weight: 4
            }).addTo(map.current);

            // Remove arc if present
            if (arcLine.current) {
                arcLine.current.remove();
                arcLine.current = null;
            }

            setRouteInfo(routeInfo);
            console.log(`API Distance: ${distanceKm.toFixed(2)} km, Price: ${finalPrice} HTG`);
            setFeeShipping(finalPrice);

        } catch (error) {
            console.error('⚠️ Erreur API TomTom:', error);
            alert('Erreur lors du calcul de la route API. Basculement sur le mode Arc.');
            setUseApiRoute(false);
        }
    };

    // Fetch pricing from backend
    const fetchPricing = async () => {
        setPricing({ basePrice: 150, pricePerKm: 130, loaded: true });
    };

    // Detect window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initialize Map
    useEffect(() => {
        if (map.current) return;

        map.current = new L.Map(mapContainer.current, {
            center: L.latLng(18.5333, -72.3333),
            zoom: zoom
        });

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd',
            maxZoom: 19
        }).addTo(map.current);

        fetchPricing();

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, []);

    // Geolocation Watch for Initial Pickup
    useEffect(() => {
        const success = (position) => {
            const { latitude, longitude } = position.coords;

            // Only set location and center map on first load
            if (!hasInitializedLocation.current) {
                setPickupLocation([latitude, longitude]);
                if (map.current) {
                    map.current.flyTo([latitude, longitude], zoom);
                }
                hasInitializedLocation.current = true;
            } else {
                // Just update position without re-centering
                setPickupLocation([latitude, longitude]);
            }
        };

        const error = (err) => {
            console.error("Geolocation error:", err);
            if (!hasInitializedLocation.current) {
                setPickupLocation([18.5333, -72.3333]);
                hasInitializedLocation.current = true;
            }
        };

        watchId.current = navigator.geolocation.watchPosition(success, error, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        });

        return () => {
            if (watchId.current !== null) {
                navigator.geolocation.clearWatch(watchId.current);
            }
        };
    }, []);

    // Update Markers, Circle, Arc/API Route and Price
    useEffect(() => {
        if (!map.current) return;

        // Pickup Marker and Circle
        if (pickupLocation) {
            if (!pickupMarker.current) {
                const pickupIcon = L.icon({
                    iconUrl: require('../data/IconImageMap/currentLocation.png'),
                    iconSize: [25, 41],
                    iconAnchor: [12, 41]
                });
                pickupMarker.current = L.marker(pickupLocation, { icon: pickupIcon }).addTo(map.current).bindPopup("Point de départ");
            } else {
                pickupMarker.current.setLatLng(pickupLocation);
            }

            // Update Circle
            if (circleRef.current) {
                circleRef.current.remove();
            }
            circleRef.current = L.circle(pickupLocation, {
                radius: radius,
                fillOpacity: 0.1,
                color: colors.secondary,
                weight: 1
            }).addTo(map.current);
        }

        // Delivery Marker
        if (deliveryLocation) {
            if (!deliveryMarker.current) {
                const deliveryIcon = L.icon({
                    iconUrl: require('../data/IconImageMap/child.png'),
                    iconSize: [32, 32],
                    iconAnchor: [16, 32]
                });
                deliveryMarker.current = L.marker(deliveryLocation, { icon: deliveryIcon }).addTo(map.current).bindPopup("Point d'arrivée");
            } else {
                deliveryMarker.current.setLatLng(deliveryLocation);
            }
        } else {
            if (deliveryMarker.current) {
                deliveryMarker.current.remove();
                deliveryMarker.current = null;
            }
        }

        // Route Display and Price Calculation
        if (pickupLocation && deliveryLocation) {
            if (useApiRoute) {
                // Use TomTom API
                calculateApiRoute(pickupLocation, deliveryLocation);
            } else {
                // Use Arc (Haversine)
                const arcCoords = createArcCoordinates(
                    pickupLocation[0], pickupLocation[1],
                    deliveryLocation[0], deliveryLocation[1]
                );

                if (arcLine.current) {
                    arcLine.current.setLatLngs(arcCoords);
                } else {
                    arcLine.current = L.polyline(arcCoords, { color: colors.secondary, weight: 4, dashArray: '5, 10' }).addTo(map.current);
                }

                // Remove API route if present
                if (apiRoutePolyline.current) {
                    apiRoutePolyline.current.remove();
                    apiRoutePolyline.current = null;
                }

                const dist = calculateHaversineDistance(pickupLocation[0], pickupLocation[1], deliveryLocation[0], deliveryLocation[1]);
                const base = pricing.basePrice;
                const perKm = 130; // Arc utilise 150 HTG/km (compense l'imprécision)

                let finalPrice = 0;
                if (dist <= 1) {
                    finalPrice = base;
                } else {
                    finalPrice = Math.ceil(dist) * perKm;
                }

                console.log(`Haversine Distance: ${dist.toFixed(2)} km, Price: ${finalPrice} HTG`);
                setFeeShipping(finalPrice);
                setRouteInfo({ distance: dist, duration: 0 });
            }
        } else {
            // No delivery location - remove routes
            if (arcLine.current) {
                arcLine.current.remove();
                arcLine.current = null;
            }
            if (apiRoutePolyline.current) {
                apiRoutePolyline.current.remove();
                apiRoutePolyline.current = null;
            }

            // Calculate price based on radius
            if (pickupLocation) {
                const dist = radius / 1000;
                const base = pricing.basePrice;
                const perKm = pricing.pricePerKm;

                let finalPrice = 0;
                if (dist <= 1) {
                    finalPrice = base;
                } else {
                    finalPrice = Math.ceil(dist) * perKm;
                }

                setFeeShipping(finalPrice);
            }
        }

    }, [pickupLocation, deliveryLocation, pricing, radius, useApiRoute]);

    const locateMe = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const { latitude, longitude } = position.coords;
                setPickupLocation([latitude, longitude]);
                map.current.flyTo([latitude, longitude], 15);
            });
        }
    };

    const confirmLocation = () => {
        const currentCenter = map.current.getCenter();
        if (isPicking === 'pickup') {
            setPickupLocation([currentCenter.lat, currentCenter.lng]);
        } else if (isPicking === 'delivery') {
            setDeliveryLocation([currentCenter.lat, currentCenter.lng]);
        }
        setIsPicking(null);
    }

    const clearDelivery = () => {
        setDeliveryLocation(null);
    }

    const toggleRouteMode = () => {
        if (pickupLocation && deliveryLocation) {
            setUseApiRoute(!useApiRoute);
        } else {
            alert("Veuillez d'abord définir un point de départ et d'arrivée");
        }
    }

    const buttonStyle = {
        backgroundColor: colors.accent,
        color: colors.black,
        borderRadius: '8px',
        fontWeight: '700',
        width: '100px',
        height: '40px',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px',
        userSelect: 'none',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        transition: 'transform 0.1s, background-color 0.2s'
    };

    const floatingButtonStyle = {
        backgroundColor: colors.accent,
        color: colors.black,
        width: 50,
        height: 50,
        borderRadius: 25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        marginBottom: 15,
        border: 'none',
        fontSize: 22
    };

    return (
        <div className="map-wrap">
            <div ref={mapContainer} className="map" style={{ zIndex: 0 }} />

            {/* Top Info Panel */}
            {!isPicking && (
                <div style={{
                    position: 'absolute',
                    flexDirection: 'column',
                    alignItems: 'center',
                    display: 'flex',
                    left: isMobile ? '5%' : '15%',
                    right: isMobile ? '5%' : '15%',
                    top: isMobile ? 10 : 20,
                    zIndex: 1,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    padding: isMobile ? '15px' : '25px',
                    borderRadius: isMobile ? '15px' : '20px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(255,255,255,0.8)'
                }}>

                    {/* Toggle Button (Top Right) */}
                    {/* <button
                        onClick={() => setSearchMode(!searchMode)}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            backgroundColor: searchMode ? colors.secondary : colors.accent,
                            color: colors.black,
                            border: 'none',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.3s ease'
                        }}
                        title={searchMode ? 'Mode Boutons' : 'Mode Recherche'}
                    >
                        {searchMode ? <UnorderedListOutlined style={{ fontSize: '18px' }} /> : <SearchOutlined style={{ fontSize: '18px' }} />}
                    </button> */}

                    <span style={{
                        fontSize: isMobile ? '24px' : '28px',
                        fontWeight: '900',
                        color: colors.black,
                        marginBottom: '8px',
                        letterSpacing: '-0.5px'
                    }}>
                        {feeShipping.toFixed(0)} HTG
                    </span>

                    <span style={{
                        fontSize: isMobile ? '13px' : '15px',
                        color: '#666',
                        marginBottom: '15px',
                        fontWeight: '500'
                    }}>
                        {pickupLocation && deliveryLocation ?
                            `${routeInfo.distance.toFixed(2)} km ${useApiRoute ? '(Route API)' : '(Vol d\'oiseau)'}` :
                            `Rayon: ${radius / 1000} km`}
                    </span>

                    {searchMode ? (
                        /* Search Mode */
                        <div style={{ width: '100%', position: 'relative' }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#fff',
                                border: `2px solid ${colors.accent}`,
                                borderRadius: '12px',
                                padding: '10px 15px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }}>
                                <SearchOutlined style={{ fontSize: '18px', color: '#999', marginRight: '10px' }} />
                                <input
                                    type="text"
                                    placeholder="Rechercher une adresse..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setShowSuggestions(e.target.value.length > 0);
                                    }}
                                    onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        outline: 'none',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        color: colors.black
                                    }}
                                />
                            </div>

                            {/* Autocomplete Suggestions */}
                            {showSuggestions && filteredAddresses.length > 0 && (
                                <div style={{
                                    position: 'absolute',
                                    top: '55px',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    zIndex: 10,
                                    border: '1px solid rgba(0,0,0,0.08)'
                                }}>
                                    {filteredAddresses.map((address, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setDeliveryLocation([address.lat, address.lon]);
                                                setSearchQuery(address.name);
                                                setShowSuggestions(false);
                                            }}
                                            style={{
                                                padding: '12px 15px',
                                                cursor: 'pointer',
                                                borderBottom: index < filteredAddresses.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <EnvironmentOutlined style={{ color: colors.accent, fontSize: '16px' }} />
                                                <span style={{ fontSize: '14px', fontWeight: '500', color: colors.black }}>
                                                    {address.name}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Button Mode */
                        <>
                            {/* Mode Switch Button */}
                            {pickupLocation && deliveryLocation && (
                                <button
                                    onClick={toggleRouteMode}
                                    style={{
                                        ...buttonStyle,
                                        width: '160px',
                                        marginBottom: '15px',
                                        backgroundColor: useApiRoute ? colors.secondary : colors.accent,
                                        fontSize: '13px',
                                        height: '42px',
                                        fontWeight: '700',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <SwapOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                                    {useApiRoute ? 'Route API' : 'Arc Simple'}
                                </button>
                            )}

                            <div style={{
                                flexDirection: 'row',
                                gap: 12,
                                display: 'flex',
                                marginTop: '5px'
                            }}>
                                <button
                                    style={{
                                        ...buttonStyle,
                                        fontSize: '13px',
                                        fontWeight: '700'
                                    }}
                                    onClick={downFunction}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                    <MinusCircleOutlined style={{ fontSize: '16px' }} />
                                </button>
                                <button
                                    style={{
                                        ...buttonStyle,
                                        fontSize: '13px',
                                        fontWeight: '700'
                                    }}
                                    onClick={upFunction}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                >
                                    <PlusCircleOutlined style={{ fontSize: '16px' }} />
                                </button>
                            </div>
                        </>
                    )}

                </div>
            )}

            {/* Powered by KIWUI Technologies Badge */}
            <div style={{
                position: 'absolute',
                bottom: isMobile ? 10 : 20,
                left: isMobile ? 10 : 20,
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: isMobile ? '6px 12px' : '8px 16px',
                borderRadius: isMobile ? '10px' : '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '6px' : '8px',
                border: '1px solid rgba(0,0,0,0.05)',
                backdropFilter: 'blur(10px)'
            }}>
                <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: colors.accent,
                    animation: 'pulse 2s infinite'
                }} />
                <span style={{
                    fontSize: isMobile ? '10px' : '11px',
                    fontWeight: '600',
                    color: colors.black,
                    letterSpacing: '0.3px'
                }}>
                    Powered by <span style={{ color: colors.accent, fontWeight: '700' }}>KIWUI</span>
                </span>
            </div>

            {/* Floating Action Buttons */}
            {!isPicking && (
                <div style={{
                    position: 'absolute',
                    bottom: isMobile ? 90 : 20,
                    right: isMobile ? 10 : 20,
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>

                    {/* Clear Delivery Button */}
                    {deliveryLocation && (
                        <button
                            style={{
                                ...floatingButtonStyle,
                                backgroundColor: '#ff4d4f',
                                width: isMobile ? 45 : 50,
                                height: isMobile ? 45 : 50,
                                borderRadius: isMobile ? '22.5px' : '25px',
                                marginBottom: isMobile ? 12 : 15
                            }}
                            onClick={clearDelivery}
                            title="Effacer destination"
                        >
                            <DeleteOutlined />
                        </button>
                    )}

                    {/* Locate Me Button */}
                    <button
                        style={{
                            ...floatingButtonStyle,
                            backgroundColor: '#fff',
                            color: colors.black,
                            width: isMobile ? 45 : 50,
                            height: isMobile ? 45 : 50,
                            borderRadius: isMobile ? '22.5px' : '25px',
                            marginBottom: isMobile ? 12 : 15
                        }}
                        onClick={locateMe}
                        title="Ma position"
                    >
                        <AimOutlined />
                    </button>

                    {/* Manual Pickup Button */}
                    <button
                        style={{
                            ...floatingButtonStyle,
                            width: isMobile ? 45 : 50,
                            height: isMobile ? 45 : 50,
                            borderRadius: isMobile ? '22.5px' : '25px',
                            marginBottom: isMobile ? 12 : 15
                        }}
                        onClick={() => setIsPicking('pickup')}
                        title="Changer départ"
                    >
                        <HomeOutlined />
                    </button>

                    {/* Delivery Button */}
                    <button
                        style={{
                            ...floatingButtonStyle,
                            width: isMobile ? 45 : 50,
                            height: isMobile ? 45 : 50,
                            borderRadius: isMobile ? '22.5px' : '25px',
                            marginBottom: 0
                        }}
                        onClick={() => setIsPicking('delivery')}
                        title="Placer arrivée"
                    >
                        <FlagOutlined />
                    </button>
                </div>
            )}

            {/* Picking UI */}
            {isPicking && (
                <>
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 2,
                        pointerEvents: 'none'
                    }}>
                        <PlusOutlined style={{ fontSize: 40, color: colors.black }} />
                        <div style={{
                            position: 'absolute',
                            top: 45,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: colors.accent,
                            color: colors.black,
                            padding: '4px 12px',
                            borderRadius: 15,
                            fontSize: 12,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                        }}>
                            {isPicking === 'pickup' ? 'Départ' : 'Arrivée'}
                        </div>
                    </div>

                    <div style={{
                        position: 'absolute',
                        bottom: 40,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                        display: 'flex',
                        gap: 20
                    }}>
                        <div
                            style={{
                                backgroundColor: '#fff',
                                padding: '10px 20px',
                                borderRadius: 30,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                cursor: 'pointer',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                            }}
                            onClick={confirmLocation}
                        >
                            <CheckOutlined style={{ color: 'green' }} />
                            <span style={{ fontWeight: 'bold' }}>Confirmer</span>
                        </div>
                        <div
                            style={{
                                backgroundColor: '#fff',
                                padding: '10px 20px',
                                borderRadius: 30,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                cursor: 'pointer',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                            }}
                            onClick={() => setIsPicking(null)}
                        >
                            <CloseOutlined style={{ color: 'red' }} />
                            <span style={{ fontWeight: 'bold' }}>Annuler</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FeeShippingMap;
